import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactInfo = () => (
  <aside className="col-span-12 md:col-span-4 p-8 md:p-10 lg:p-12 border-b md:border-b-0 md:border-r border-black/10 bg-[#000000] text-white">
    <span className="overline" style={{ color: "rgba(255,255,255,0.6)" }}>
      Prefer to skip the form?
    </span>
    <a
      href="mailto:info@structuremedia.me"
      data-testid="contact-email-link"
      className="block font-serif-display text-2xl md:text-3xl mt-3 link-underline"
    >
      info@structuremedia.me
    </a>
    <a
      href="tel:+12078087711"
      data-testid="contact-phone-link"
      className="block font-serif-display text-xl md:text-2xl mt-2 link-underline text-white/85"
    >
      207-808-7711
    </a>

    <div className="hairline my-8" style={{ background: "rgba(255,255,255,0.18)" }} />

    <span className="overline" style={{ color: "rgba(255,255,255,0.6)" }}>
      Response
    </span>
    <p className="mt-2 text-[14px] text-white/75 leading-relaxed max-w-xs">
      We reply to every inquiry within one business day, and most are
      on the calendar within 48 hours.
    </p>

    <div className="hairline my-8" style={{ background: "rgba(255,255,255,0.18)" }} />

    <span className="overline" style={{ color: "rgba(255,255,255,0.6)" }}>
      Studio
    </span>
    <address className="not-italic mt-2 font-serif-display text-xl leading-tight">
      120 Main Street #243<br />
      <span className="italic-display">Saco, Maine</span>
    </address>
  </aside>
);

const EMPTY_FORM = { name: "", email: "", phone: "", service: "", message: "" };

const getErrorDetail = (err) => {
  const detail =
    err?.response?.data?.detail?.[0]?.msg ||
    err?.response?.data?.detail ||
    "Something went wrong. Please try again.";
  return typeof detail === "string" ? detail : "Submission failed.";
};

const ContactForm = () => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submitLabel = () => {
    if (submitting) return "Sending…";
    if (submitted) return "Sent — send another?";
    return "Send inquiry";
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email, and a short message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thanks — we’ll be in touch within 1 business day.");
      setSubmitted(true);
      setForm(EMPTY_FORM);
    } catch (err) {
      toast.error(getErrorDetail(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={onSubmit}
      data-testid="contact-form"
      className="col-span-12 md:col-span-8 p-8 md:p-10 lg:p-12 grid grid-cols-12 gap-x-6 gap-y-2"
    >
      <div className="col-span-12 mb-2">
        <span className="overline">Inquire</span>
      </div>

      <div className="col-span-12 md:col-span-6">
        <label className="overline" htmlFor="c-name">Name</label>
        <input
          id="c-name"
          data-testid="contact-input-name"
          className="editorial-input"
          value={form.name}
          onChange={update("name")}
          placeholder="Your full name"
          required
        />
      </div>
      <div className="col-span-12 md:col-span-6">
        <label className="overline" htmlFor="c-email">Email</label>
        <input
          id="c-email"
          type="email"
          data-testid="contact-input-email"
          className="editorial-input"
          value={form.email}
          onChange={update("email")}
          placeholder="you@brokerage.com"
          required
        />
      </div>
      <div className="col-span-12 md:col-span-6 mt-4">
        <label className="overline" htmlFor="c-phone">Phone (optional)</label>
        <input
          id="c-phone"
          data-testid="contact-input-phone"
          className="editorial-input"
          value={form.phone}
          onChange={update("phone")}
          placeholder="+1 (___) ___ — ____"
        />
      </div>
      <div className="col-span-12 mt-4">
        <label className="overline" htmlFor="c-message">What services are you interested in learning more about?</label>
        <textarea
          id="c-message"
          data-testid="contact-input-message"
          className="editorial-input"
          rows={4}
          value={form.message}
          onChange={update("message")}
          placeholder="Tell us a little about what you're after — photo, video, drone, 3D, brand work, or anything else."
          required
        />
      </div>

      <div className="col-span-12 mt-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <span className="text-[12px] text-[#5a6470] max-w-md">
          By submitting, you agree to be contacted about your inquiry.
          We never share details with third parties.
        </span>
        <button
          type="submit"
          disabled={submitting}
          data-testid="contact-submit-button"
          className="group inline-flex items-center justify-between gap-6 bg-[#c9a878] text-black px-7 py-4 text-[12px] tracking-[0.22em] uppercase font-medium hover:bg-black hover:text-white transition-colors duration-300 disabled:opacity-60"
        >
          {submitLabel()}
          <span aria-hidden className="transition-transform duration-500 group-hover:translate-x-1">→</span>
        </button>
      </div>
    </form>
  );
};

export default function Contact() {
  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 md:py-36 border-t border-black/10 bg-[#f6f6f4]"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-6 mb-12 md:mb-16">
          <div className="col-span-12 md:col-span-3">
            <span className="overline">07 — Get in touch</span>
          </div>
          <div className="col-span-12 md:col-span-9">
            <h2 className="font-serif-display text-4xl sm:text-6xl md:text-7xl lg:text-[120px] leading-[0.95] tracking-[-0.02em]">
              Lights. Camera.{" "}
              <span className="italic-display text-[#c9a878]">Address?</span>
            </h2>
            <p className="mt-6 max-w-xl text-[15px] md:text-[17px] leading-[1.7] text-[#3a3a3a]">
              Send us the address — we'll handle the rest. Most inquiries are
              on the calendar within 48 hours.
            </p>
          </div>
        </div>

        <div className="relative bg-white border border-black/10 shadow-[0_30px_80px_-40px_rgba(0,0,0,0.25)]">
          <div aria-hidden className="absolute left-0 top-0 h-1 w-24 bg-[#c9a878]" />
          <div className="grid grid-cols-12">
            <ContactInfo />
            <ContactForm />
          </div>
        </div>
      </div>
    </section>
  );
}
